import { useState } from "react";

import { Box, BoxProps } from "@mui/material";
import styled from "@mui/material/styles/styled";

import { planSupportsVideoRecording } from "@app/analytics/account/Billing/utils";
import { UpsellModal } from "@app/analytics/components/UpsellModal";
import useBillingContext from "@app/analytics/hooks/billing/useBillingContext";
import VideoSelectionOverlay from "@app/analytics/MeetingReport/components/VideoSelectionOverlay";

interface ThumbnailContainerProps extends BoxProps {
  thumbnailUrl?: string | null;
}

const ThumbnailContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "thumbnailUrl",
})<ThumbnailContainerProps>(({ thumbnailUrl, theme }) => ({
  backgroundColor: "#0000008D",
  backgroundSize: "contain",
  aspectRatio: 16 / 9,
  position: "relative",
  overflow: "hidden",
  backgroundImage: `url("${thumbnailUrl}")`,
  backgroundRepeat: "no-repeat",
  backgroundPosition: "center",

  [theme.breakpoints.down("md")]: {
    width: "100%",
    height: "213px",
  },
  [theme.breakpoints.up("md")]: {
    borderRadius: theme.corners.small,
  },
  [theme.breakpoints.up("lg")]: {
    // special case small vertical screens
    height: "213px",
    // and then everyone else
    "@media (min-height: 780px)": {
      height: "320px",
    },
  },
}));

const PlaybackUnauthorized = ({
  thumbnailUrl,
}: {
  thumbnailUrl?: string | null;
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const { currentPlan, isEligibleForLegacyPricing } = useBillingContext();
  const openModal = () => setModalOpen(true);

  // the user could hit this state if they have a stale recording token, in which case we
  // don't want to ask them to upgrade, since they would simply need to refresh the page
  // to see the video again
  const hasStaleRecordingToken = planSupportsVideoRecording(currentPlan.type);

  return (
    <Box sx={{ zIndex: 4 }}>
      <ThumbnailContainer thumbnailUrl={thumbnailUrl}>
        <VideoSelectionOverlay
          options={[
            {
              label: "Trailer",
              icon: "🎬",
              locked: true,
              disabled: hasStaleRecordingToken,
              onClick: openModal,
            },
            {
              label: "Highlights",
              icon: "⚡️",
              locked: true,
              disabled: hasStaleRecordingToken,
              onClick: openModal,
            },
            {
              label: "Recording",
              icon: "⏱",
              locked: true,
              disabled: hasStaleRecordingToken,
              onClick: openModal,
            },
          ]}
        />
      </ThumbnailContainer>
      <UpsellModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        currentPlan={currentPlan.type}
        useLegacyPrices={isEligibleForLegacyPricing}
      />
    </Box>
  );
};

export default PlaybackUnauthorized;
